import React, { useRef, useEffect, useState } from 'react';
import { useChat } from '../../contexts/ChatContext';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import SearchToggle from './SearchToggle';
import Sidebar from './Sidebar';
import ApiError from '../ApiError';
import StorageWarning from '../UI/StorageWarning';
import NotificationPrompt from '../UI/NotificationPrompt';
import { useNotificationPrompt } from '../../hooks/useNotificationPrompt';
import { FiMenu } from 'react-icons/fi';

// Check if we're in dev mode (based on env variable set in .env.dev vs .env.production)
const IS_DEV_MODE = process.env.REACT_APP_DEV_MODE === 'true';

const ChatInterface: React.FC = () => {
  const { messages, isLoading, apiError, sendMessage, stopMessage, enableMockMode, retryConnection, pendingClarification, searchEnrichmentEnabled, setSearchEnrichmentEnabled } = useChat();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false); // For mobile sidebar
  const [sidebarVisible, setSidebarVisible] = useState(false); // For sidebar animation state
  
  // Notification prompt management
  const {
    shouldShowPrompt,
    showPrompt,
    dismissPrompt,
    enableNotifications,
  } = useNotificationPrompt();

  // Show notification prompt when user sends their first message with loading state
  useEffect(() => {
    // Show prompt when loading starts and user has messages (indicating they sent a query)
    if (isLoading && messages.length > 0) {
      // Add a small delay to avoid showing immediately
      const timer = setTimeout(() => {
        showPrompt();
      }, 2000); // Show after 2 seconds of loading
      
      return () => clearTimeout(timer);
    }
  }, [isLoading, messages.length, showPrompt]);

  // Animate sidebar out before unmounting
  const handleSidebarClose = () => {
    setSidebarVisible(false);
    setTimeout(() => setSidebarOpen(false), 300); // match animation duration
  };

  // When opening, set both states immediately
  const handleSidebarOpen = () => {
    setSidebarOpen(true);
    setTimeout(() => setSidebarVisible(true), 10); // allow mount before animating in
  };

  // // Only auto-scroll when an assistant message is added (not user messages)
  // useEffect(() => {
  //   if (messages.length > 0) {
  //     const lastMessage = messages[messages.length - 1];
  //     // Only scroll if the last message is from the assistant
  //     if (lastMessage.role === 'assistant') {
  //       messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  //     }
  //   }
  // }, [messages]);
  
  // Check connection on component mount if not using mock API
  useEffect(() => {
    // In production, always check connection
    if (process.env.NODE_ENV === 'production') {
      retryConnection();
      return;
    }
    
    // In development, check localStorage for mock API setting
    const useMockApi = localStorage.getItem('useMockApi') === 'true';
    if (!useMockApi) {
      retryConnection();
    }
  }, [retryConnection]);

  return (
    <div className="flex h-screen relative">
      {/* Notification Prompt */}
      <NotificationPrompt
        show={shouldShowPrompt}
        onDismiss={dismissPrompt}
        onEnable={enableNotifications}
      />
      
      {/* Storage Warning - Fixed position overlay */}
      <StorageWarning />
      
      {/* Sidebar - responsive */}
      {/* Hidden on small screens, shown on md+; overlay on mobile when open */}
      <div className="hidden md:block h-full">
        <Sidebar />
      </div>
      {/* Mobile sidebar overlay (animated panel + backdrop) */}
      {/* Only render overlay if sidebarOpen, to avoid pointer-events and layout issues */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            onClick={handleSidebarClose}
            className={`absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300 ease-in-out ${sidebarVisible ? 'opacity-100' : 'opacity-0'}`}
            aria-hidden="true"
          />
          {/* Sliding panel */}
          <div
            className={`relative w-64 max-w-full h-full bg-secondary dark:bg-dark-secondary shadow-2xl rounded-r-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${sidebarVisible ? 'translate-x-0' : '-translate-x-full'}`}
            style={{ willChange: 'transform' }}
          >
            <Sidebar onClose={handleSidebarClose} />
          </div>
        </div>
      )}
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col h-full min-h-0 min-w-0">
        {/* Mobile top bar with menu and title */}
        {/* {!sidebarOpen && ( */}
          <div className="md:hidden sticky top-0 bg-white dark:bg-dark-primary shadow-sm z-30">
            <div className="flex items-center justify-center h-14 relative">
              {/* Hamburger button (left) */}
              <button
                className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-md bg-white hover:bg-neutral dark:bg-dark-accent dark:text-white dark:hover:bg-dark-border shadow-md"
                onClick={handleSidebarOpen}
                aria-label="Open sidebar"
              >
                <FiMenu size={24} />
              </button>
              {/* Centered title */}
              <span className="text-xl tracking-tight font-bold flex items-center">
                <span className="text-primary">Shipment</span>
                <span className="font-light text-text-primary dark:text-white ml-1">CoCaptain</span>
              </span>
              {/* Invisible spacer to balance the hamburger button */}
              <span className="absolute right-2 top-1/2 -translate-y-1/2 p-2 opacity-0">
                <FiMenu size={24} />
              </span>
            </div>
          </div>
        {/* )} */}
        {/* Messages Container */}
        {/* <div className={`flex-1 relative overflow-y-auto overflow-x-hidden py-4 px-4 sm:px-4 md:px-6 min-h-0 bg-neutral-light dark:bg-dark-primary`}>
          {messages.length === 0 && !isLoading && !apiError && (
            <div
              className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_top_right,theme(colors.primary.DEFAULT/0.33)_0%,theme(colors.primary.DEFAULT/0.10)_25%,theme(colors.primary.DEFAULT/0.05)_42%,transparent_69%)] animate-pulse"
              aria-hidden="true"
            />
          )}

          <div className="w-full max-w-none relative z-10"> */}
        <div className={`flex-1 overflow-y-auto overflow-x-hidden py-4 px-4 sm:px-4 md:px-6 min-h-0 ${messages.length === 0 && !isLoading && !apiError ? 'dark:bg-[radial-gradient(ellipse_at_top_right,theme(colors.primary.DEFAULT/0.33)_0%,theme(colors.primary.DEFAULT/0.10)_25%,theme(colors.primary.DEFAULT/0.05)_42%,transparent_69%)] bg-[radial-gradient(ellipse_at_top_right,theme(colors.primary.DEFAULT/0.25)_0%,theme(colors.primary.DEFAULT/0.10)_25%,theme(colors.primary.DEFAULT/0.05)_33%,transparent_50%)]' : 'bg-neutral-light dark:bg-dark-primary'}`}>
          <div className="w-full max-w-none">
            <div className={`h-full ${isLoading ? '' : 'flex items-center justify-center'} ${apiError ? 'py-12': 'py-0'}`}>
            {apiError ? (
                <ApiError 
                  error={apiError} 
                  onRetry={retryConnection} 
                  onEnableMockMode={enableMockMode} 
                />
              ) : (
                <MessageList messages={messages} isLoading={isLoading} />
              )}
            </div>
            <div ref={messagesEndRef} />
          </div>
        </div>
        
        {/* Input Area */}
        <div className="p-1 sm:p-4">
          {/* bg-white dark:bg-dark-primary border-t border-neutral-dark dark:border-dark-border */}
          <div className="max-w-full md:max-w-3xl mx-auto">
            {/* Dev-only Search Enrichment Toggle - positioned top-right above input */}
            {IS_DEV_MODE && (
              <div className="flex justify-end mb-2">
                <SearchToggle 
                  enabled={searchEnrichmentEnabled} 
                  onToggle={setSearchEnrichmentEnabled} 
                />
              </div>
            )}
            <MessageInput 
              onSendMessage={sendMessage} 
              onStopMessage={stopMessage}
              isLoading={isLoading} 
              pendingClarification={pendingClarification}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
